import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, MapPin, ChevronLeft, ChevronRight } from "lucide-react";
import { fetchFeaturedEvents } from "@/lib/api";

/**
 * Featured events box for the dealer storefront right sidebar.
 * Mirrors the "Join Our National CellCode Tour" card on mindfulminerals.com.
 */
export default function EventsFeaturedBox({ dealerSlug = "" }) {
  const [events, setEvents] = useState([]);
  const [idx, setIdx] = useState(0);
  const dq = dealerSlug ? `?d=${dealerSlug}` : "";
  const seeAllHref = dealerSlug ? `/d/${dealerSlug}/events` : "/events";

  useEffect(() => {
    let alive = true;
    fetchFeaturedEvents()
      .then((e) => alive && setEvents(e || []))
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (events.length === 0) return null;
  const safe = ((idx % events.length) + events.length) % events.length;
  const ev = events[safe];
  const d = ev.date_time ? new Date(ev.date_time) : null;

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white shadow-sm overflow-hidden" data-testid="events-featured-box">
      <div className="bg-gradient-to-br from-emerald-600 to-emerald-800 text-white px-5 py-4">
        <div className="text-base font-bold leading-tight">Join Our Wellness Events</div>
        <div className="text-[11px] text-emerald-100 mt-0.5">Reserve your spot at an upcoming event</div>
      </div>
      <div className="p-4 relative">
        <Link to={`/events/${ev.slug}${dq}`} className="block group rounded-xl border border-zinc-200 hover:border-emerald-300 hover:shadow-sm transition-all overflow-hidden" data-testid={`featured-event-${ev.slug}`}>
          {ev.image ? (
            <div className="aspect-[16/10] bg-zinc-50">
              <img src={ev.image} alt={ev.title} className="w-full h-full object-cover" loading="lazy" onError={(e) => (e.currentTarget.style.display = "none")} />
            </div>
          ) : (
            <div className="aspect-[16/10] bg-gradient-to-br from-violet-100 to-emerald-100 flex items-center justify-center">
              <CalendarDays size={40} className="text-emerald-600/50" />
            </div>
          )}
          <div className="p-3">
            <div className="flex items-center gap-2 text-[11px] font-semibold text-emerald-700">
              <CalendarDays size={12} />
              {d ? d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ev.date_label}
            </div>
            <h4 className="font-bold text-zinc-900 text-sm leading-snug mt-1 line-clamp-2">{ev.title}</h4>
            {ev.city && (
              <div className="text-[11px] text-zinc-500 mt-1 inline-flex items-center gap-1">
                <MapPin size={11} /> {ev.city}{ev.state ? `, ${ev.state}` : ""}
              </div>
            )}
          </div>
        </Link>

        {events.length > 1 && (
          <>
            <button onClick={() => setIdx(safe - 1)} className="absolute top-[38%] -left-1 -translate-y-1/2 w-8 h-8 rounded-full bg-white border border-zinc-200 shadow-md flex items-center justify-center text-zinc-600 hover:text-emerald-700" aria-label="Previous event" data-testid="featured-event-prev">
              <ChevronLeft size={16} />
            </button>
            <button onClick={() => setIdx(safe + 1)} className="absolute top-[38%] -right-1 -translate-y-1/2 w-8 h-8 rounded-full bg-white border border-zinc-200 shadow-md flex items-center justify-center text-zinc-600 hover:text-emerald-700" aria-label="Next event" data-testid="featured-event-next">
              <ChevronRight size={16} />
            </button>
            <div className="flex items-center justify-center gap-1.5 mt-3">
              {events.map((_, i) => (
                <button key={i} onClick={() => setIdx(i)} aria-label={`Show event ${i + 1}`} className={`h-2 rounded-full transition-all ${i === safe ? "bg-emerald-600 w-4" : "bg-zinc-300 w-2 hover:bg-zinc-400"}`} />
              ))}
            </div>
          </>
        )}

        <Link to={seeAllHref} className="block text-center text-sm font-semibold text-emerald-700 hover:text-emerald-800 mt-3" data-testid="featured-events-see-all">
          See all events →
        </Link>
      </div>
    </div>
  );
}
